/**
 * ============================================================================
 * ChangeTypePicker — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: Step 1 of the CC proposal wizard. Presents the 9 supported CC
 *          types as a grid of large pickable cards (radio semantics) with a
 *          short description per option so the author understands the
 *          downstream impact set scope before committing.
 *
 * A11Y:
 *   - role="radiogroup" with named labelling; each card is a radio button.
 *   - The whole tile is clickable; focus ring on the underlying input.
 * ============================================================================ */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChangeRequestProposal.module.css';
import type { ChangeType } from './useCreateProposalMutation';

const CHANGE_TYPES: ChangeType[] = [
  'DOCUMENT_CONTENT',
  'DOCUMENT_OWNERSHIP',
  'EQUIPMENT_RELOCATION',
  'EQUIPMENT_COMPONENT',
  'SUPPLIER_CHANGE',
  'SUPPLIER_MATERIAL',
  'VALIDATION_STATUS',
  'REGULATORY',
  'SYSTEM_CONFIG',
];

export interface ChangeTypePickerProps {
  value: ChangeType | null;
  onChange: (value: ChangeType) => void;
}

export function ChangeTypePicker({ value, onChange }: ChangeTypePickerProps) {
  const { t } = useTranslation();
  const groupId = useId();

  return (
    <fieldset
      className={styles.typeGrid}
      role="radiogroup"
      aria-label={t('changeControl.proposal.typePicker.groupAria', {
        defaultValue: 'Change type',
      })}
    >
      <legend className={styles.srOnly}>
        {t('changeControl.proposal.typePicker.legend', {
          defaultValue: 'Pick the type of change you are proposing.',
        })}
      </legend>
      {CHANGE_TYPES.map((ct) => {
        const inputId = `${groupId}-${ct}`;
        const selected = value === ct;
        return (
          <label
            key={ct}
            htmlFor={inputId}
            className={`${styles.typeTile} ${selected ? styles.typeTileSelected : ''}`}
          >
            <input
              id={inputId}
              type="radio"
              name={`${groupId}-radio`}
              value={ct}
              checked={selected}
              onChange={() => onChange(ct)}
              className={styles.typeRadio}
              aria-describedby={`${inputId}-desc`}
            />
            <span className={styles.typeTileLabel}>
              {t(`changeControl.proposal.typePicker.types.${ct}.label`, {
                defaultValue: ct,
              })}
            </span>
            <span id={`${inputId}-desc`} className={styles.typeTileDesc}>
              {t(`changeControl.proposal.typePicker.types.${ct}.desc`, {
                defaultValue: ct,
              })}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
